/**
 * Shared Chart.js machinery for every observation chart in the app — the
 * developmental-goal trajectory chart (trajectory-chart.ts) and the
 * monitored-event review charts (monitored-chart.ts) are both callers.
 *
 * Centralising this is what makes the two chart families "the same tech":
 * one Chart.js registration, one singleton HTML tooltip (#chartTooltip), one
 * zoom/pan config, one note-vs-no-note colour rule. Each chart family then
 * differs only in how it shapes points and its y-axis.
 *
 * Extracted from trajectory-chart.ts (OMON-201 chart unification).
 */

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

import {
  broadcastRange, onRangeChange, resetGroupZoom, formatRange, type SyncGroup,
} from "./chart-sync";
import { nowMarkerFraction, nowLabelFits } from "./now-marker";
import "chartjs-adapter-date-fns";

// Register Chart.js controllers and plugins ONCE for the whole app. Both
// chart modules import chart-kit, so importing either guarantees registration.
Chart.register(
  LineController, LineElement, PointElement,
  LinearScale, CategoryScale, TimeScale,
  Tooltip, Legend, Filler,
  zoomPlugin,
);

// Re-export the registered Chart so callers share this exact instance.
export { Chart };

// Re-exported so chart modules keep importing zoom machinery from one place.
export {
  clearSyncGroup, registerForSync, onRangeChange, resetGroupZoom, broadcastRange,
  formatRange, setSyncKey, restoreRememberedView, type SyncGroup, type SyncableChart,
} from "./chart-sync";

export const CHART_COLORS = {
  planned: "#9d0a6d",            // plum — dashed planned line
  plannedAssessment: "#d1d5db",  // grey — dashed line during the assessment period
  actual: "#15803d",             // green — solid line points (no note)
  actualNote: "#2563eb",         // blue — points that have a note attached
  attempted: "#dc2626",          // dark red — OMON-275 score 0: attempted, unable to start
  breach: "#dc2626",             // red — over threshold (target/escalate breach)
  now: "#435363",                // slate — OMON-308 "today" line (NOT red: see nowLinePlugin)
  grid: "#f3f4f6",
  catBg: "#f3f4f6",
  catBorder: "#f5e0ee",
  catText: "#9d0a6d",
} as const;

/**
 * The app-wide point colour rule: blue when the recording has a carer note,
 * green otherwise. Shared so dev-goal and monitored charts colour identically.
 */
export function pointColor(hasNote: boolean): string {
  return hasNote ? CHART_COLORS.actualNote : CHART_COLORS.actual;
}

// Loose shape of the Chart.js external-tooltip context — the fields we read.
export interface TooltipContext {
  chart: Chart;
  tooltip: {
    opacity: number;
    title?: string[];
    dataPoints?: Array<{
      datasetIndex: number;
      dataIndex: number;
      parsed: { y: number | null };
      raw?: unknown;
    }>;
    caretX: number;
    caretY: number;
  };
}

/**
 * Render the shared singleton HTML tooltip. Owns the #chartTooltip element,
 * the show/hide on opacity, and viewport-clamped positioning — identical
 * behaviour for every chart. `buildInner` supplies the chart-specific HTML
 * (the rows) from the tooltip model.
 */
export function renderExternalTooltip(
  context: TooltipContext,
  buildInner: (tooltip: TooltipContext["tooltip"]) => string,
): void {
  const { chart, tooltip } = context;

  let el = document.getElementById("chartTooltip") as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "chartTooltip";
    el.className = "chart-tooltip hidden";
    document.body.appendChild(el);
  }

  if (tooltip.opacity === 0) {
    el.classList.add("hidden");
    return;
  }

  const inner = buildInner(tooltip);
  if (!inner) {
    // Nothing meaningful to show for the nearest point (e.g. a threshold line).
    el.classList.add("hidden");
    return;
  }
  el.classList.remove("hidden");
  el.innerHTML = inner;

  // Position using viewport coordinates (fixed positioning).
  const canvasRect = chart.canvas.getBoundingClientRect();
  const caretX = canvasRect.left + tooltip.caretX;
  const caretY = canvasRect.top + tooltip.caretY;
  const ttWidth = el.offsetWidth;
  const ttHeight = el.offsetHeight;

  let left = caretX - ttWidth / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - ttWidth - 8));

  let top = caretY + 16;
  if (top + ttHeight > window.innerHeight - 8) {
    top = caretY - ttHeight - 12;
  }
  top = Math.max(8, top);

  el.style.left = left + "px";
  el.style.top = top + "px";
}

/**
 * The shared zoom/pan plugin config: ctrl+wheel / pinch to zoom on x, shift+drag
 * to pan, clamped to [min, max] so zoom-out reverts to the full window and pan
 * can't drift past the data. (OMON-052 / OMON-043 behaviour.)
 *
 * Pass `group` to link the chart's zoom to its siblings.
 */
export function zoomConfig(minMs: number, maxMs: number, group?: SyncGroup) {
  const relay = group
    ? ({ chart }: { chart: Chart }) => broadcastRange(group, chart as unknown as never)
    : undefined;
  return {
    limits: { x: { min: minMs, max: maxMs } },
    zoom: {
      wheel: { enabled: true, modifierKey: "ctrl" as const },
      pinch: { enabled: true },
      mode: "x" as const,
      ...(relay ? { onZoom: relay } : {}),
    },
    pan: {
      enabled: true,
      mode: "x" as const,
      modifierKey: "shift" as const,
      ...(relay ? { onPan: relay } : {}),
    },
  };
}

/**
 * A per-chart plugin that draws horizontal threshold line(s) with a right-aligned
 * label, in the plot area, behind nothing (afterDatasetsDraw). Used by the tally
 * count chart for "target ≤ N" / "escalate > N". Drawing it as a plugin (not a
 * dataset) keeps it out of the nearest-point tooltip interaction entirely.
 */
export interface ThresholdLine {
  value: number;
  label: string;
  color?: string;
}

export function thresholdPlugin(lines: ThresholdLine[]) {
  return {
    id: "thresholdLines",
    afterDatasetsDraw(chart: Chart) {
      const { ctx, chartArea, scales } = chart as unknown as {
        ctx: CanvasRenderingContext2D;
        chartArea: { left: number; right: number };
        scales: { y?: { getPixelForValue(v: number): number } };
      };
      const yScale = scales.y;
      if (!yScale) return;
      for (const line of lines) {
        const y = yScale.getPixelForValue(line.value);
        const color = line.color || CHART_COLORS.breach;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 4]);
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.7;
        ctx.lineWidth = 1.5;
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.font = "10px -apple-system, sans-serif";
        ctx.textAlign = "right";
        ctx.textBaseline = "bottom";
        ctx.fillText(line.label, chartArea.right - 2, y - 3);
        ctx.restore();
      }
    },
  };
}

/**
 * OMON-308: a per-chart plugin drawing a dotted vertical "now" line with a small
 * label, redrawn on every zoom/pan frame and suppressed when today is off-screen.
 *
 * ☠️ NOT RED, deliberately — unlike the Connect swimlane's marker, which is
 * `#dc2626`. In this palette red is already spoken for twice: CHART_COLORS.breach
 * (over target/escalation threshold) and CHART_COLORS.attempted (score 0, client
 * unable to start). A red line down a care chart would read as an alert about the
 * client rather than a date. Slate `#435363` is the plan builder's existing
 * now-line colour (.builder-now-line), so the read view and the edit view now
 * agree — they used to disagree, the builder having a marker and the read view
 * none.
 */
export function nowLinePlugin() {
  return {
    id: "nowLine",
    afterDatasetsDraw(chart: Chart) {
      const { ctx, chartArea, scales } = chart as unknown as {
        ctx: CanvasRenderingContext2D;
        chartArea: { left: number; right: number; top: number; bottom: number };
        scales: { x?: { min: number; max: number } };
      };
      const x = scales.x;
      if (!x) return;
      // Read the clock at DRAW time, not at build time: the marker then advances
      // by itself across a long-lived page and stays put under zoom and pan.
      const f = nowMarkerFraction({ min: x.min, max: x.max }, Date.now());
      if (f === null) return;

      const px = chartArea.left + f * (chartArea.right - chartArea.left);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([2, 3]);
      ctx.strokeStyle = CHART_COLORS.now;
      ctx.globalAlpha = 0.8;
      ctx.lineWidth = 1.5;
      ctx.moveTo(px, chartArea.top);
      ctx.lineTo(px, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      if (nowLabelFits(f)) {
        ctx.globalAlpha = 1;
        ctx.font = "bold 9px -apple-system, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        // A backdrop chip so the label stays readable over gridlines and points,
        // matching .builder-now-line::before in index.html.template.
        const label = "now";
        const w = ctx.measureText(label).width;
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillRect(px + 2, chartArea.top, w + 3, 11);
        ctx.fillStyle = CHART_COLORS.now;
        ctx.fillText(label, px + 3, chartArea.top + 1);
      }
      ctx.restore();
    },
  };
}

// Re-rendering a view rebuilds its charts, so the previous subscription has to
// go or each render stacks another listener. One per group.
const rangeUnsubscribers = new Map<SyncGroup, () => void>();

/**
 * Show the visible span and a reset control for a chart group.
 *
 * Linked zoom keeps a group's charts agreeing with each other; this is what
 * stops them agreeing on something you have forgotten you asked for. Both
 * elements stay hidden at full span so the default view is uncluttered.
 */
export function wireZoomControls(group: SyncGroup, badgeId: string, resetId: string): void {
  const badge = document.getElementById(badgeId);
  const reset = document.getElementById(resetId);
  if (!badge || !reset) return;

  rangeUnsubscribers.get(group)?.();
  badge.hidden = true;
  reset.hidden = true;

  rangeUnsubscribers.set(group, onRangeChange(group, (min, max, zoomed) => {
    badge.textContent = zoomed ? `Showing ${formatRange(min, max)}` : "";
    badge.hidden = !zoomed;
    reset.hidden = !zoomed;
  }));

  (reset as HTMLButtonElement).onclick = () => resetGroupZoom(group);
}
